import type { CentreStudentSession } from '@ielts-buddy/shared'
import { useCallback, useEffect, useState } from 'react'
import { fetchStudentSessions } from '../shared/api.js'
import { AppNav } from '../shared/ui/AppNav.js'
import { Card } from '../shared/ui/Card.js'
import { ErrorState } from '../shared/ui/ErrorState.js'
import { HeroCard } from '../shared/ui/HeroCard.js'
import { LoadingDots } from '../shared/ui/LoadingDots.js'

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function formatKind(kind: CentreStudentSession['kind']) {
  const labels: Record<CentreStudentSession['kind'], string> = {
    warm_up: 'Warm Up',
    section: 'Section',
    full_test: 'Full Test',
  }
  return labels[kind]
}

function formatModule(module: CentreStudentSession['module']) {
  const labels: Record<CentreStudentSession['module'], string> = {
    speaking: 'Speaking',
    writing: 'Writing',
    listening: 'Listening',
    reading: 'Reading',
  }
  return labels[module]
}

function StatusBadge({ status }: { status: CentreStudentSession['status'] }) {
  const config = {
    completed: {
      label: 'Done',
      className: 'bg-brand-primary-soft text-brand-primary-strong',
    },
    abandoned: {
      label: 'Abandoned',
      className: 'bg-brand-error-soft text-brand-error',
    },
    in_progress: {
      label: 'In Progress',
      className: 'bg-brand-warning-soft text-brand-warning',
    },
  }
  const { label, className } = config[status]
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${className}`}
    >
      {label}
    </span>
  )
}

interface StudentScreenProps {
  userId: string
  firstName: string
}

export function StudentScreen({ userId, firstName }: StudentScreenProps) {
  const [sessions, setSessions] = useState<CentreStudentSession[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { sessions: data } = await fetchStudentSessions(userId)
      setSessions(data)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    void load()
  }, [load])

  const completed = sessions.filter((s) => s.status === 'completed')
  const avgBand =
    completed.length > 0
      ? completed.reduce((sum, s) => sum + (s.overallBand ?? 0), 0) /
        completed.length
      : null

  return (
    <div className="min-h-screen flex flex-col">
      <AppNav breadcrumb={firstName} />

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8 animate-fade-in">
        {loading ? (
          <div className="flex justify-center py-20">
            <LoadingDots />
          </div>
        ) : error ? (
          <ErrorState
            title="Failed to load sessions"
            message={error}
            primaryAction={{ label: 'Retry', onClick: load }}
          />
        ) : (
          <div className="flex flex-col gap-5">
            <HeroCard
              label="Student"
              title={firstName}
              metrics={[
                { value: sessions.length, label: 'Sessions' },
                {
                  value: avgBand != null ? avgBand.toFixed(1) : '—',
                  label: 'Avg Band',
                },
                { value: completed.length, label: 'Completed' },
              ]}
            />

            {/* Session history */}
            <Card tone="surface" className="overflow-hidden">
              <div className="px-5 py-4 border-b border-brand-border flex items-center justify-between">
                <h2 className="font-semibold text-brand-text text-sm">
                  Session History
                </h2>
                {sessions.length > 0 && (
                  <span className="text-xs text-brand-text-subtle bg-brand-bg-soft border border-brand-border rounded-full px-2 py-0.5">
                    {sessions.length}
                  </span>
                )}
              </div>
              {sessions.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-14 text-center">
                  <div className="w-10 h-10 rounded-full bg-brand-bg-soft border border-brand-border flex items-center justify-center text-brand-text-subtle text-lg">
                    📋
                  </div>
                  <p className="text-brand-text-muted text-sm font-medium">
                    No sessions yet
                  </p>
                  <p className="text-brand-text-subtle text-xs">
                    Sessions will appear here once the student starts practicing
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-brand-border bg-brand-bg-soft">
                        <th className="text-left px-5 py-3 text-xs font-medium text-brand-text-muted">
                          Date
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-brand-text-muted">
                          Skill
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-brand-text-muted hidden sm:table-cell">
                          Type
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-brand-text-muted">
                          Band
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-brand-text-muted">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...sessions].reverse().map((s, i) => (
                        <tr
                          key={s.id}
                          className={
                            i < sessions.length - 1
                              ? 'border-b border-brand-border'
                              : ''
                          }
                        >
                          <td className="px-5 py-3.5 text-brand-text-muted">
                            {formatDate(s.startedAt)}
                          </td>
                          <td className="px-5 py-3.5 text-brand-text-muted">
                            {formatModule(s.module)}
                          </td>
                          <td className="px-5 py-3.5 text-brand-text-muted hidden sm:table-cell">
                            {formatKind(s.kind)}
                          </td>
                          <td className="px-5 py-3.5">
                            <span
                              className={`font-semibold ${s.overallBand != null ? 'text-brand-primary-strong' : 'text-brand-text-muted'}`}
                            >
                              {s.overallBand != null
                                ? s.overallBand.toFixed(1)
                                : '—'}
                            </span>
                          </td>
                          <td className="px-5 py-3.5">
                            <StatusBadge status={s.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        )}
      </main>
    </div>
  )
}
